import { parse } from 'date-fns';
import moment from 'moment';
import { dataFormat, paramsListArr } from '../Assets/Constants/json_constants.js';
import { fetchMenuDetail, getGrouping } from '../Services/api-config.js';
import {
	resetAuth,
	updateUserInfo,
	updateUserSetting
} from '../Store/AuthSlice/auth-slice.js';
import { AUTHKEY, REFRESHKEY } from './keys.js';

export const checkAccessPermissions = (userData, requiredPermission) => {
	let hasPermission = true;
	if (requiredPermission) {
		hasPermission = userData?.roles?.some((role) =>
			role?.permissions?.some(
				(permission) => permission.permission_name === requiredPermission
			)
		);
	}

	return hasPermission;
};

export const validateMobileNumber = (value) =>
	value.replaceAll(/\D/g, '').replace(/^[0-5]/, '');
export const validatePincode = (value) =>
	value.replaceAll(/\D/g, '').replace(/^0/, '');
export const validateName = (value) =>
	value
		?.trimStart()
		?.replaceAll(/[^a-zA-Z\s]/g, '')
		?.replace('  ', ' ');

export const validateAphaNumberUpperCase = (value) =>
	value.replaceAll(/[^a-zA-Z\d]/g, '');
export const validateAphaNumber = (value) =>
	value
		.trimStart()
		.replaceAll(/[^a-zA-Z\d\s]/g, '')
		.replaceAll(/\s+/g, ' ');
export const validateEmailLowerCase = (value) =>
	value.trimStart().toLowerCase().replace(' ', '');
export const validateCity = (value) =>
	value.trimStart().replaceAll(/[^a-zA-Z]/g, '');

export const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
	const byteCharacters = window.atob(
		b64Data.replace(/^data:image\/(png|jpeg|jpg);base64,/, '')
	);
	const byteArrays = [];

	for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
		const slice = byteCharacters.slice(offset, offset + sliceSize);

		const byteNumbers = Array.from({ length: slice.length });
		for (let i = 0; i < slice.length; i++) {
			byteNumbers[i] = slice.codePointAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);
		byteArrays.push(byteArray);
	}

	const blob = new Blob(byteArrays, { type: contentType });

	return blob;
};

export function downloadBase64File(
	contentBase64,
	fileName,
	type,
	fileType = 'application'
) {
	let linkSource = '';
	if (type === 'csv') {
		const decodedData = window.atob(contentBase64);

		const uint8Array = new Uint8Array(decodedData.length);
		for (let i = 0; i < decodedData.length; i++) {
			uint8Array[i] = decodedData.codePointAt(i);
		}

		const blob = new Blob([uint8Array], { type: 'text/csv' });

		const downloadLink = window.document.createElement('a');
		downloadLink.href = URL.createObjectURL(blob);
		downloadLink.download = fileName;
		window.document.body.append(downloadLink);

		downloadLink.click();
		downloadLink.remove();
	} else {
		linkSource
			= type === 'pdf'
				? contentBase64
				: `data:${fileType}/${type};base64,${contentBase64}`;

		const downloadLink = window.document.createElement('a');
		window.document.body.append(downloadLink);
		downloadLink.href = linkSource;
		downloadLink.target = '_self';
		downloadLink.download = fileName;
		downloadLink.click();
	}
}

export function downloadXlsxFile(contentBase64, fileName) {
	try {
		// Decode the base64 string
		const decodedData = window.atob(contentBase64);
		const uint8Array = Uint8Array.from(decodedData, (char) =>
			char.codePointAt(0)
		);

		// Create a Blob with MIME type for .xlsx files
		const blob = new Blob([uint8Array], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		});
		const linkSource = URL.createObjectURL(blob);

		// Create a download link and trigger the download
		const downloadLink = window.document.createElement('a');
		downloadLink.href = linkSource;
		downloadLink.download = fileName;
		window.document.body.append(downloadLink);
		downloadLink.click();
		downloadLink.remove();

		// Revoke the object URL to free up memory
		URL.revokeObjectURL(linkSource);
	} catch (error) {
		console.error('Failed to download the .xlsx file:', error);
	}
}

export const fetchMenuDetails = async (dispatch) => {
	const response = await fetchMenuDetail();
	if (response?.status === 200) {
		const menu = response?.data?.[0].permissions;

		dispatch(
			updateUserInfo({
				menu,
				userId: response?.data?.[0].userId,
				brandingChange: false,
				profileImageChanged: true,
				branding: response?.data?.[0].branding,
				orgId: response?.data?.[0].orgId,
				userName: response?.data?.[0].userName,
				userType: response?.data?.[0].userType,
				isAdmin: response?.data?.[0]?.isSuperAdmin,
				approvalEsignFlow: response?.data?.[0]?.approvalEsignFlow,
				servicesType: response?.data?.[0]?.servicesType
			})
		);
	}

	return response;
};

export const fetchDataGrpNameForBranch = async (data, dispatch) => {
	let groupName = '';
	const val = data;
	groupName
		= val?.groupName.toLowerCase() === 'custom' ? val?.alias : val?.groupName;

	const param = [];

	for (const data of paramsListArr) {
		let saveObj = {
			id: data?.id,
			title: data?.title,
			type: data?.type,
			width: data?.width,
			placeholder: data?.placeholder,
			regexPattern: data?.regexPattern,
			error: data?.error,
			value: data?.value
		};
		if (val?.[data?.title?.toLowerCase() + 'Required'] !== undefined) {
			saveObj = {
				...saveObj,
				required: val?.[data?.title?.toLowerCase() + 'Required']
			};
		}

		if (val?.[data?.title?.toLowerCase() + 'Enabled'] !== undefined) {
			saveObj = {
				...saveObj,
				visible: val?.[data?.title?.toLowerCase() + 'Enabled']
			};
		}

		const obj = {};
		obj.id = saveObj.id;
		obj.title = saveObj.title;
		obj.required = saveObj.required;
		obj.visible = saveObj.visible;
		obj.type = saveObj.type;
		obj.width = saveObj.width;
		obj.placeholder = saveObj.placeholder;
		obj.regexPattern = saveObj.regexPattern;
		obj.error = saveObj.error;
		obj.value = saveObj.value;

		param.push({ ...obj });
	}

	dispatch(
		updateUserSetting({
			group: groupName,
			paramsArr: param
		})
	);
	return groupName;
};

export const fetchDataGrpName = async (dispatch) => {
	const response = await getGrouping();
	if (response?.status === 200) {
		let groupName = '';
		const val = response.data?.find((val) => val.enableGroup === true);
		groupName
			= val?.groupName.toLowerCase() === 'custom' ? val?.alias : val?.groupName;

		const param = [];

		for (const data of paramsListArr) {
			let saveObj = {
				id: data?.id,
				title: data?.title,
				type: data?.type,
				width: data?.width,
				placeholder: data?.placeholder,
				regexPattern: data?.regexPattern,
				error: data?.error,
				value: data?.value
			};
			if (val?.[data?.title?.toLowerCase() + 'Required'] !== undefined) {
				saveObj = {
					...saveObj,
					required: val?.[data?.title?.toLowerCase() + 'Required']
				};
			}

			if (val?.[data?.title?.toLowerCase() + 'Enabled'] !== undefined) {
				saveObj = {
					...saveObj,
					visible: val?.[data?.title?.toLowerCase() + 'Enabled']
				};
			}

			const obj = {};
			obj.id = saveObj.id;
			obj.title = saveObj.title;
			obj.required = saveObj.required;
			obj.visible = saveObj.visible;
			obj.type = saveObj.type;
			obj.width = saveObj.width;
			obj.placeholder = saveObj.placeholder;
			obj.regexPattern = saveObj.regexPattern;
			obj.error = saveObj.error;
			obj.value = saveObj.value;

			param.push({ ...obj });
		}

		dispatch(
			updateUserSetting({
				group: groupName,
				paramsArr: param
			})
		);
		return groupName;
	}
};

export const parseJwt = (token) => {
	try {
		return JSON.parse(window.atob(token.split('.')[1]));
	} catch {
		return null;
	}
};

export function areStartingLettersCapital(str) {
	const regex = /\b[A-Z]/g;
	return regex.test(str);
}

export const transformToCapitalise = (value) => {
	if (value) {
		return value
			.split(' ')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(' ');
	}

	return '';
};

export const transformOneWordToCapitalise = (value) =>
	value
		? value
			.split(' ')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ')
		: '';

export const getTimeFromDate = (value) => {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return null;
	}

	return date?.toISOString()?.split('T')[1]?.split('.')[0];
};

export const getDateOnly = (value) => {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return null;
	}

	return date?.toLocaleDateString();
};

export const isValidDate = (date) => !Number.isNaN(Date.parse(date));

export const handleLogoutUtil = async (dispatch) => {
	try {
		localStorage.removeItem(AUTHKEY);
		localStorage.removeItem(REFRESHKEY);
		dispatch(resetAuth());

		// Hard redirect (clears history)
		window.location.replace('/login');
	} catch (error) {
		console.error('Logout failed', error);
	}
};

export const convertDateStringToDate = (dateString) => {
	const isAMPMFormat = dateString.includes('PM') || dateString.includes('AM');

	const formatOptions = isAMPMFormat ? 'hh:mm a, MMM do, yyyy' : 'MMM do, yyyy';

	try {
		const parsedDate = parse(dateString, formatOptions, new Date());
		return parsedDate;
	} catch (error) {
		console.error('Error parsing date:', error);
		return null;
	}
};

export const debounce = (func, delay) => {
	let timeoutId;
	return function (...args) {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => {
			func.apply(this, args);
		}, delay);
	};
};

export const CommonTextInputStyles = {
	divStyles: { backgroundColor: 'white', borderRadius: '8px' },
	width: '300px',
	height: '40px',
	borderColor: '#ECF0E5',
	removeImageLeftBorder: true,
	flex: 1
};

export const CommonDropdownStyles = {
	divStyles: { backgroundColor: 'white', borderRadius: '8px' },
	borderRadius: '8px',
	className: 'medium6',
	width: '300px',
	height: '40px',
	flex: 1
};

export function formatValueforPan(value = '') {
	if (!value) {
		return '';
	}

	const cleanedValue = value.toUpperCase().replaceAll(/[^A-Z\d]/g, '');

	let formattedValue = '';

	formattedValue += cleanedValue.slice(0, 5).replaceAll(/[^A-Z]/g, '');

	formattedValue += cleanedValue.slice(5, 9).replaceAll(/\D/g, '');

	if (cleanedValue.length >= 10) {
		formattedValue += cleanedValue[9].replaceAll(/[^A-Z]/g, '');
	}

	return formattedValue;
}

export function formatValueForPincode(value = '') {
	if (!value) {
		return '';
	}

	const cleanedValue = value.replaceAll(/\D/g, '');

	let formattedValue = '';

	formattedValue += cleanedValue.slice(0, 6);

	return formattedValue;
}

export const formatValueToNumbers = (value) => {
	const numericValue = value.replaceAll(/\D/g, '');
	return numericValue;
};

export const formatValueToNumbersWithDecimals = (value) => {
	let numericValue = value.replaceAll(/[^\d.]/g, '');

	const parts = numericValue.split('.');
	if (parts.length > 2) {
		numericValue = parts[0] + '.' + parts.slice(1).join('').slice(0, 2);
	} else if (parts.length === 2) {
		numericValue = parts[0] + '.' + parts[1].slice(0, 2);
	}

	return numericValue;
};

export function formatAlphanumeric(value = '') {
	const sanitizedValue = value.trimStart().replaceAll(/[^a-zA-Z\d]/g, '');
	return sanitizedValue;
}

export function formatSingleSpace(value = '') {
	if (!value) {
		return '';
	}

	const formattedValue = value.trimStart().replaceAll(/\s+/g, ' ');

	return formattedValue;
}

export const createPayload = (page, limit, filter, search, sort) => ({
	page: page || 1,
	limit: limit || 10,
	...(filter?.doFilter && {
		filterValue: filter.value,
		filterColumnName: filter.column || filter.field
	}),
	...(sort?.doSort && {
		sortingType: sort.type,
		sortingColumnName: sort.field
	}),
	...(search?.doSearch && { search: search.value })
});

export const createFilterPayload = (filterSearch) => {
	const temp = {
		value: filterSearch?.value || '',
		column: filterSearch?.field || ''
	};

	temp.doFilter = Boolean(filterSearch?.value);

	return temp;
};

export const createSearchPayload = (searchQuery) => {
	const tempSearch = {
		doSearch: false
	};
	if (searchQuery) {
		tempSearch.doSearch = true;
		tempSearch.value = searchQuery;
	}

	return tempSearch;
};

export const createSortingPayload = (sortingType) => {
	let temp = { doSort: false };
	if (sortingType?.length > 0) {
		temp = {
			field: sortingType?.[0].field,
			type: sortingType?.[0].sort,
			doSort: true
		};
	}

	return temp;
};

export const createPayloads = (filterSearch, searchQuery, sortingType) => {
	const tempFilter = createFilterPayload(filterSearch);
	const tempSearch = createSearchPayload(searchQuery);
	const tempSorting = createSortingPayload(sortingType);

	return {
		filter: tempFilter,
		search: tempSearch,
		sorting: tempSorting
	};
};

export const isSuccessfulResponse = (response) => response?.status === 200;

export const deepEqual = (obj1, obj2) => {
	if (obj1 === obj2) {
		return true;
	}

	if (
		typeof obj1 !== 'object'
		|| typeof obj2 !== 'object'
		|| obj1 === null
		|| obj2 === null
	) {
		return false;
	}

	const keys1 = Object.keys(obj1);
	const keys2 = Object.keys(obj2);

	if (keys1.length !== keys2.length) {
		return false;
	}

	for (const key of keys1) {
		if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
			return false;
		}
	}

	return true;
};

export const getErrorMessage = (
	response,
	defaultMessage = 'Something went wrong'
) => {
	const message
		= response?.message
		|| response?.error?.errorMessage
		|| response?.error
		|| defaultMessage;
	return message;
};

export const redirectToRoute = (data = [], menu = {}, menuItem = []) => {
	const temp = data.filter((data) => {
		if (
			data.title === 'UAM'
			&& menuItem.includes('Approval')
			&& Object.keys(menu.Approval || {}).includes('uam_approval_request')
		) {
			return true;
		}

		if (data.title === 'Dashboard') {
			const tempMenu = Object?.values(menu?.Dashboard || {}).flatMap((item) =>
				item.filter((data) => data.permissionName !== 'new_esign')
			);
			return tempMenu.length > 0;
		}

		if (
			data.title === 'Wallet'
			&& menuItem.includes('Approval')
			&& Object.keys(menu.Approval || {}).includes('wallet_approval_request')
		) {
			return true;
		}
	});
	return temp;
};

export const routerData = (data = [], menu = {}, menuItem = []) => {
	const dashboardApprovalExists = Boolean(
		menu?.Approval?.dashboard_approval_request
	);

	const dashboardPermissions = Object.values(menu?.Dashboard || {}).flat();

	const hasUamApprovalRequest = Boolean(menu?.Approval?.uam_approval_request);

	const hasNonBulkDashboardPermission = dashboardPermissions.some(
		(perm) => perm.permissionName !== 'bulk_new_esign'
	);

	const hasWalletApprovalRequest = Boolean(
		menu?.Approval?.wallet_approval_request
	);

	const temp = data.filter((route) => {
		if (route.title === 'Dashboard') {
			return dashboardApprovalExists || hasNonBulkDashboardPermission;
		}

		if (route.title === 'UAM' && hasUamApprovalRequest) {
			return true;
		}

		if (route.title === 'Wallet' && hasWalletApprovalRequest) {
			return true;
		}

		return menuItem.includes(route.title) || route.title === 'Logo';
	});
	return temp;
};

export const getMobileValue = (isMobile, value, defaultValue) => {
	if (isMobile) {
		return value;
	}

	return defaultValue;
};

export const getTruthyValue = (condition, trueValue = '', falseValue = '') => {
	if (condition) {
		return trueValue;
	}

	return falseValue;
};

export const validateUrl = (url = '') => {
	try {
		const normalizedUrl
			= url.startsWith('http://') || url.startsWith('https://')
				? url
				: `http://${url}`;
		const newUrl = new URL(normalizedUrl);
		if (newUrl) {
			return true;
		}
	} catch {
		return false;
	}
};

export const isImageFile = (url) => {
	const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'];

	const hasImageExtension = imageExtensions.some((ext) =>
		url?.toLowerCase().endsWith(ext)
	);

	const isBase64Image = /^data:image\/(png|jpg|jpeg|gif|bmp|webp);base64,/.test(
		url
	);
	return hasImageExtension || isBase64Image;
};

export const buttonStyle = {
	style: { borderColor: '#B7D423' },
	className: 'semiBold14',
	width: '200px',
	padding: '10px 16px',
	borderRadius: '8px',
	height: '40px',
	maxHeight: '40px',
	backgroundColor: 'white',
	fontWeight: 400,
	sx: {
		borderColor: '#B7D423',
		borderWidth: '2px',
		borderStyle: 'solid',
		backgroundColor: '#FFFFFF',
		color: '#B7D423',
		fontWeight: 400,
		textcolor: '#000000',
		'&:hover': {
			backgroundColor: '#FFFFFF'
		}
	}
};

export const mandatoryDocuments = [
	'Copy of Cancel Cheque',
	'Company PAN / TAN Certificate',
	'GST Certificate',
	'User Pan Card',
	'Company Address Proof',
	'Customer Registration Form'
];

export const additionalDocuments = [
	'Application Form',
	'Audit Check List',
	'Entity KYC',
	'Others'
];

export const decodeJwtPayload = (token) => {
	if (!token || typeof token !== 'string') {
		return null;
	}

	try {
		const parts = token.split('.');
		if (parts.length !== 3) {
			return null;
		}

		const base64Url = parts[1];
		const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');

		// Decode base64 string manually
		const binaryString = window.atob(base64);
		const bytes = new Uint8Array(binaryString.length);
		for (let i = 0; i < binaryString.length; i++) {
			const codePoint = binaryString.codePointAt(i);
			if (codePoint !== undefined) {
				bytes[i] = codePoint;
			}
		}

		// Decode bytes to string
		const decoded = new TextDecoder().decode(bytes);

		// Decode URI component safely
		const jsonPayload = decodeURIComponent(
			decoded
				.split('')
				.map((c) => '%' + ('00' + c.codePointAt(0).toString(16)).slice(-2))
				.join('')
		);

		return JSON.parse(jsonPayload);
	} catch (error) {
		console.error('Failed to decode JWT:', error);
		return null;
	}
};

export const multiply = (value, factor) => value * factor;
export const subtract = (value, factor) => value - factor;

export const getStatusFilterOptionsById = (navData, tabId) => {
	for (const item of navData) {
		const found = item.subTabs.find((subTab) => subTab.id === tabId);
		if (found) {
			return found.statusFilterOptions || [];
		}
	}

	return [];
};

export const parseCustomDateFormat = (dateString) =>
	moment(dateString, 'h:mm A, MMM Do, YYYY').toISOString();

export function formatFeedbackInput(input, maxLength = 250) {
	const sanitized = input
		.replaceAll(/[^\w(),\-/\s]/g, '')
		.replaceAll(/\s+/g, ' ')
		.trimStart();

	return sanitized.length <= maxLength ? sanitized : sanitized.slice(0, maxLength);
}

export const formatDataGridColumns = (apiColumns) => apiColumns.map((col) => {
	const columnType = dataFormat[col.field] ? dataFormat[col.field].type : 'string';
	const columnOptions = columnType === 'singleSelect'
		? dataFormat[col.field]?.valueOptions ?? []
		: [];

	return {
		field: col.field,
		headerName: col.headerName,
		sortable: col.sorting,
		filterable: col.filter,
		disableColumnMenu: !col.filter,
		flex: 1,
		type: columnType,
		...(columnType === 'number' && {
			headerAlign: 'left'
		}),
		...(columnType === 'singleSelect' && {
			valueOptions: columnOptions,
			renderCell: (params) => params.value
		}),
		valueGetter(params) {
			const value = params?.value;
			if (value) {
				if (columnType === 'date') {
					const formattedDate = parseCustomDateFormat(value);
					return formattedDate ? new Date(formattedDate) : null;
				}

				return value;
			}

			return null;
		}
	};
});

export const handleResponseError = (response, showToast) => {
	const errorMessage = getErrorMessage(response);
	showToast('error', errorMessage);
	return errorMessage;
};
